import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch, getFavoriteByUserId } from "../redux";
import ReviewModal from "../components/ui/ReviewModal";
import {
  Home, Wallet, MessageCircle, User, FileText, Shield, 
  Bell, Search, Bookmark, LogOut, Settings, Lock, 
  AlertCircle, Briefcase, FileCheck, Menu, X, ChevronRight, 
  Globe, User2, 
  Settings2, ChartAreaIcon, Star, UserCheck, UserSearchIcon
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import { Avatar } from "../components/ui/avatar";
import PolicyModals from "../components/ui/PolicyModals";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { MdReviews } from "react-icons/md";
import SharedHeader4 from "../Headers/SharedHeader4";
import SocialMediaLinks from "../components/ui/SocialMediaLinks";

const Favorites222 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProfessionalTermsModal, setShowProfessionalTermsModal] = useState(false);
  
  const { user, isAuthenticated } = useSelector((state: any) => state.auth);
  const { currentUserFavorite, loading } = useSelector((state: any) => state.favorites);
  const { userReviews, userReviewsLoading, userReviewsError } = useSelector((state: any) => state.reviews);
  const dispatch = useAppDispatch();
  const [favoriteClientsData, setFavoriteClientsData] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedClientForReview, setSelectedClientForReview] = useState(null);

  // Process user data from userIds object
  const processClientData = useCallback((userIds: any[]) => {
    if (!userIds || userIds.length === 0) return [];
    
    setClientsLoading(true);
    try {
      return userIds.map(user => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        avatar: user.photo?.path,
        industry: user.company?.industry?.name || t("favorites.notSpecified"),
        totalProjects: user.company?.totalProjects || 0,
        location: user.address || t("favorites.noLocation"),
        isActive: user.status?.id === 1, // Assuming status.id === 1 means active
        projectTypes: user.company?.projectTypes || [t("favorites.generalServices")],
        company: user.company,
        rating: user.averageRating || 0,
        reviewsCount: user.reviewsCount || 0,
        reviewsList: user.reviewsList || []
      }));
    } catch (error) {
      return [];
    } finally {
      setClientsLoading(false);
    }
  }, []);

  // Load favorites when component mounts
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      dispatch(getFavoriteByUserId(user.id));
    }
  }, [user?.id]);

  // Process user data when favorites are loaded
  useEffect(() => {
    if (isAuthenticated && currentUserFavorite?.userIds) {
      const userIds = currentUserFavorite.userIds;
      const userData = processClientData(userIds);
      setFavoriteClientsData(userData);
    } else if (!isAuthenticated) {
      // For unauthenticated users, we can't get full user data, so show empty
      setFavoriteClientsData([]);
    } else {
      setFavoriteClientsData([]);
    }
  }, [currentUserFavorite, isAuthenticated, processClientData]);

  // Use the fetched favorite clients data
  const favoriteClients = favoriteClientsData;

  // StarRatingDisplay component for showing user ratings
  const StarRatingDisplay = ({ rating, count, size = "sm" }: { rating: number; count: number; size?: "sm" | "md" | "lg" }) => {
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5", 
      lg: "h-6 w-6"
    };

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1">
        <div className="flex">
          {[...Array(fullStars)].map((_, i) => (
            <Star key={i} className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
          ))}
          {hasHalfStar && (
            <div className="relative">
              <Star className={`${sizeClasses[size]} text-gray-300`} />
              <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                <Star className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
              </div>
            </div>
          )}
          {[...Array(emptyStars)].map((_, i) => (
            <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className={`${sizeClasses[size]} text-gray-300`} />
          ))}
        </div>
        <span className="text-sm font-medium text-blue-700">
          {rating.toFixed(1)} ({count} {t("favorites.reviews")})
        </span>
      </div>
    );
  };


  const StoreLinks = () => (
    <div className="flex justify-center gap-2 pt-6 px-4">
      <a
        href="https://play.google.com/store/apps/details?id=com.kokobeo"
        className="w-1/2"
      >
        <img
          src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
          alt={t("favorites.getItOnGooglePlay")}
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "120px" }}
        />
      </a>
      <a href="https://apps.apple.com/app/kokobeo" className="w-1/2">
        <img
          src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
          alt={t("favorites.downloadOnAppStore")}
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "120px" }}
        />
      </a>
    </div>
  );
  
  const SocialIcons = () => (
    <SocialMediaLinks className="flex justify-center space-x-3 pt-4 px-4 pb-6 border-t border-gray-100 mt-2" iconSize="h-4 w-4" />
  );

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      {/* Fixed Header */}
      <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16 w-full">
        <SharedHeader4 />
      </div>

      <div className="flex min-h-[calc(100vh-64px)] pt-16">

        {/* Main Content */}
        <div className="flex-1 w-full">
          <main className="p-4 lg:p-8 overflow-y-auto h-[calc(100vh-64px)] w-full">
            <div className="max-w-7xl mx-auto space-y-8 w-full">
              {/* Page Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{t("favorites.clientsTitle")}</h1>
                  <p className="text-gray-600 mt-1">
                    {t("favorites.clientsSubtitle")}
                  </p>
                </div>
                <div className="flex items-center gap-2 lg:hidden">
                  {/* <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setIsMobileMenuOpen(true)}
                  >
                    <Menu className="h-5 w-5 mr-2" />
                    {t("favorites.menu")}
                  </Button> */}
                </div>
              </div>

              {/* Clients Card */}
              <Card className="overflow-hidden w-full">
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2 text-green-600" />
                    {t("favorites.yourFavoriteClients")}
                  </h2>
                  <Button variant="outline" size="sm">
                    <Star className="h-4 w-4 mr-2" />
                    {t("favorites.manageFavorites")}
                  </Button>
                </div>

                <div className="p-4 bg-green-50 flex items-center gap-3">
                  <Search className="h-5 w-5 text-green-600" />
                  <p className="text-sm text-green-700">
                    {t("favorites.clientsDesc")}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {favoriteClients.length > 0 ? (
                    favoriteClients.map((client) => (
                      <Card key={client.id} className="overflow-hidden hover:shadow-md transition-shadow">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {client.avatar ? (
                                <img 
                                  src={client.avatar} 
                                  alt={client.name} 
                                  className="h-12 w-12 rounded-full object-cover border-2 border-white"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center">
                                  <User className="h-6 w-6 text-gray-500" />
                                </div>
                              )}
                              {client.isActive && (
                                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{client.name}</h3>
                              <p className="text-sm text-gray-600">{client.industry}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                          </Button>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            {client.reviewsCount > 0 ? (
                              <button
                                onClick={() => {
                                  setSelectedClientForReview(client);
                                  setShowReviewModal(true);
                                }}
                                className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                                title={t("favorites.clickToViewAllReviews")}
                              >
                                <StarRatingDisplay 
                                  rating={client.rating} 
                                  count={client.reviewsCount} 
                                  size="sm" 
                                />
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-gray-300" />
                                <span className="text-sm text-gray-500">{t("favorites.noReviews")}</span>
                              </div>
                            )}
                            <span className="text-gray-300">|</span>
                            <span className="text-sm text-gray-600">{client.totalProjects} {t("favorites.totalProjects")}</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-3 flex items-center">
                            <Globe className="h-4 w-4 mr-1 text-gray-500" />
                            {client.location}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {client.projectTypes.map((type, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex justify-between">
                          <Button size="sm" variant="outline" onClick={()=>{navigate(`/client/profile/${client.id}`);}}>{t("favorites.viewProfile")}</Button>
                          <Button size="sm" onClick={()=>{
                            navigate("/MessageAndVideoCall?selectedUserId=" + client.id);
                          }}>{t("favorites.contact")}</Button>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-full p-8 text-center">
                      <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <Briefcase className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t("favorites.noFavoriteClients")}</h3>
                      <p className="text-gray-600 mb-4">{t("favorites.noFavoriteClientsDesc")}</p>
                      <Button>{t("favorites.browseJobs")}</Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-white border-t mt-auto w-full">
            <div className="max-w-7xl mx-auto">
              <SharedFooter3 />
            </div>
          </footer>
        </div>
      </div>

      {/* Modals */}
      <PolicyModals
        showPrivacyModal={showPrivacyModal}
        showTermsModal={showTermsModal}
        showProfessionalTermsModal={showProfessionalTermsModal}
        onClosePrivacy={() => setShowPrivacyModal(false)}
        onCloseTerms={() => setShowTermsModal(false)}
        onCloseProfessionalTerms={() => setShowProfessionalTermsModal(false)}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        professional={selectedClientForReview ? {
          id: selectedClientForReview.id,
          name: selectedClientForReview.name,
          firstName: selectedClientForReview.name.split(" ")[0],
          lastName: selectedClientForReview.name.split(" ").slice(1).join(" "),
          title: selectedClientForReview.industry,
          photo: selectedClientForReview.avatar ? { path: selectedClientForReview.avatar } : undefined,
          averageRating: selectedClientForReview.rating,
          reviewsCount: selectedClientForReview.reviewsCount,
          reviewsList: selectedClientForReview.reviewsList
        } : null}
      />
    </div>
  );
};

export default Favorites222;